import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { ChatRoutePendingShell } from "@/components/agents/agent-loading-shells";
import { useAgents } from "@/lib/api";
import { pickPeekSearch } from "@/lib/peek-search";

/**
 * Agent-less entry point to chat: `/{owner}/chat?new=1&prompt=...`.
 *
 * Exists so a caller outside the SPA can open a seeded conversation without
 * first resolving which agent to address. The Owletto Mac app is the caller
 * that needs it — when a recording stops it opens this URL with the finalized
 * segment paths in `prompt`, and it knows the workspace slug but has never
 * had to know an agent id.
 *
 * Resolves the same default the Activity composer uses (the system agent, else
 * the first) and forwards every search param untouched, so `?prompt=` still
 * lands as a DRAFT in the composer rather than being sent. Seeding a message
 * the user never typed must stay one keystroke away from them.
 */
export const Route = createFileRoute("/$owner/chat/")({
	validateSearch: (
		search: Record<string, unknown>,
	): Record<string, string | true | undefined> => {
		const out: Record<string, string | true | undefined> = {};
		if (search.new === true || search.new === "1" || search.new === "true") {
			out.new = true;
		}
		if (typeof search.prompt === "string" && search.prompt.length > 0) {
			out.prompt = search.prompt;
		}
		Object.assign(out, pickPeekSearch(search));
		return out;
	},
	component: ChatEntryRedirect,
});

/**
 * Where `/{owner}/chat` sends the browser, or null while no agent is known.
 *
 * Pure so the property that actually matters can be pinned: `search` is
 * forwarded UNTOUCHED. Dropping it here would strip the `prompt` the Mac app
 * put in the URL and the handoff would silently open an empty composer — a
 * failure with no error anywhere.
 */
export function chatEntryTarget(
	agents: readonly { agentId: string; system?: boolean }[],
	owner: string,
	search: Record<string, string | true | undefined>,
) {
	const target = agents.find((agent) => agent.system) ?? agents[0];
	if (!target) return null;
	return {
		to: "/$owner/chat/$agentId",
		params: { owner, agentId: target.agentId },
		search,
		replace: true,
	} as const;
}

function ChatEntryRedirect() {
	const { owner } = Route.useParams();
	const search = Route.useSearch();
	const navigate = useNavigate();
	const { data: agents = [], isLoading } = useAgents();

	const target = chatEntryTarget(agents, owner, search);

	useEffect(() => {
		if (!target) return;
		void navigate(target);
	}, [navigate, target]);

	// No agents at all is a real workspace state, not a load failure — send the
	// user somewhere they can act instead of spinning forever.
	useEffect(() => {
		if (isLoading || agents.length > 0) return;
		void navigate({ to: "/$owner/agents", params: { owner }, replace: true });
	}, [agents.length, isLoading, navigate, owner]);

	return <ChatRoutePendingShell />;
}
